import * as React from 'react';

import {
  proficientDiagnostic,
  formatDateToShortFormat,
  diagnosticLevelToActivityId,
  diagnosticLevelToUnitTemplateId,
  STARTER,
  INTERMEDIATE,
  ADVANCED,
  TROUBLE_PROCESSING_MESSAGE,
  DemonstratedProficiencyFlag,
  RecommendedFlag,
} from '../shared'
import { StudentHistoryInterface, LeveledAssignmentsInterface, DiagnosticsAssignedInterface, } from '../interfaces'
import { ButtonSpinner, Tooltip, titleCase } from '../../../../../Shared/index'
import { requestPost, } from '../../../../../../modules/request'
import DemoGuardedButton from '../../../shared/demoGuardedButton';

interface IndividualStudentRecommendationCardProps {
  classroomId: number;
  diagnosticLevel: string;
  previouslyAssignedDiagnosticsByLevel: DiagnosticsAssignedInterface;
  recommendations: LeveledAssignmentsInterface;
  studentHistory: StudentHistoryInterface;
  getPreviouslyAssignedDiagnosticDataForClassroom: () => void;
  useELLDiagnostics: boolean;
  firstName: string;
  setSnackbarText: (string) => void;
  setShowSnackbar: (boolean) => void;
}

const IndividualStudentRecommendationCard = ({
  classroomId,
  diagnosticLevel,
  previouslyAssignedDiagnosticsByLevel,
  recommendations,
  studentHistory,
  getPreviouslyAssignedDiagnosticDataForClassroom,
  useELLDiagnostics,
  firstName,
  setSnackbarText,
  setShowSnackbar,
}: IndividualStudentRecommendationCardProps) => {
  const [assigning, setAssigning] = React.useState<Boolean>(false)

  const previewLink = (diagnosticLevel) => {
    const activityId = diagnosticLevelToActivityId(useELLDiagnostics)[diagnosticLevel]
    return `/activity_sessions/anonymous?activity_id=${activityId}`
  }

  const recommendation = recommendations[diagnosticLevel]
  const studentLearningSequences = studentHistory.student_learning_sequences[diagnosticLevel]

  const diagnosticLevelForPreviousAssignments = useELLDiagnostics ? `ell_${diagnosticLevel}` : diagnosticLevel

  const previousAssignmentsForLevel = previouslyAssignedDiagnosticsByLevel[diagnosticLevelForPreviousAssignments]

  const previousAssignmentRecords = previousAssignmentsForLevel?.filter(record => record.user_id === studentHistory.user_id) || []

  const previousAssignmentRecordToUse = previousAssignmentRecords.find(record => record.completed_at) || previousAssignmentRecords[0]

  const studentIsRecommendedThisDiagnostic = recommendation.student_ids.includes(studentHistory.user_id)

  const proficientDiagnosticRecord = proficientDiagnostic(studentLearningSequences);

  const descriptionParagraphCopy = () => {
    if (studentIsRecommendedThisDiagnostic) {
      return previousAssignmentRecordToUse ? `You've already assigned this recommended diagnostic to ${firstName}. You're all set!` : `We recommend assigning this diagnostic, as ${firstName} has not yet demonstrated full proficiency in most skills.`
    } else if (proficientDiagnosticRecord) {
      return `${firstName} demonstrated full proficiency in most skills on this diagnostic on ${formatDateToShortFormat(proficientDiagnosticRecord.completed_at)}.`
    } else if (diagnosticLevel === INTERMEDIATE) {
      return `We don't recommend assigning this diagnostic yet. ${firstName} should first demonstrate full proficiency in most skills on the Starter Diagnostic.`
    }

    const proficientStarter = proficientDiagnostic(studentHistory.student_learning_sequences[STARTER])
    const proficientIntermediate = proficientDiagnostic(studentHistory.student_learning_sequences[INTERMEDIATE])

    let recommendedDiagnosticsCopy = 'Starter and Intermediate diagnostics'

    if (proficientStarter && !proficientIntermediate) {
      recommendedDiagnosticsCopy = 'Intermediate diagnostic'
    } else if (!proficientStarter && proficientIntermediate) {
      recommendedDiagnosticsCopy = 'Starter diagnostic'
    }
    return `We don't recommend assigning this diagnostic yet. ${firstName} should first demonstrate full proficiency in most skills on the ${recommendedDiagnosticsCopy}.`
  }

  const assignOrViewResultsButton = () => {
    if (!previousAssignmentRecordToUse) {
      return <DemoGuardedButton ariaLabel={`Assign Diagnostic to ${firstName}`} className="quill-button green contained small focus-on-light" onClick={assignDiagnostics}>{assigning ? <ButtonSpinner variant="light" /> : 'Assign'}</DemoGuardedButton>
    }

    const { activity_id, completed_at, visible } = previousAssignmentRecordToUse

    if (completed_at && visible) {
      return <a className="quill-button green contained small focus-on-light" href={`/teachers/progress_reports/diagnostic_reports/diagnostics/${activity_id}/classroom/${classroomId}/responses/${studentHistory.user_id}`} rel="noopener noreferrer" target="_blank">View results</a>
    } else if (completed_at) {
      return (
        <Tooltip
          tooltipText="Results aren’t available for this diagnostic. It may have been deleted or unassigned."
          tooltipTriggerText={<button className="quill-button green contained small disabled focus-on-light" disabled={true} type="button">View results</button>}
        />
      )
    }

    return (
      <Tooltip
        tooltipText="Results aren’t available yet as this student hasn’t completed this diagnostic."
        tooltipTriggerText={<button className="quill-button green contained small disabled focus-on-light" disabled={true} type="button">View results</button>}
      />
    )

  }

  function assignDiagnostics() {
    setAssigning(true)

    const params = {
      classroom_id: classroomId,
      diagnostics: [{
        unit_template_id: diagnosticLevelToUnitTemplateId(useELLDiagnostics)[diagnosticLevel],
        student_ids: (previousAssignmentsForLevel?.map(assignment => assignment.student_id) || []).concat(studentHistory.user_id)
      }]
    }

    requestPost('/teachers/progress_reports/assign_diagnostics/', params, (data) => {
      getPreviouslyAssignedDiagnosticDataForClassroom()
      setAssigning(false)
      setSnackbarText('Diagnostic assigned')
      setShowSnackbar(true)
    }, (data) => {
      alert(TROUBLE_PROCESSING_MESSAGE);
      setAssigning(false)
    })
  }

  return (
    <section aria-labelledby={`${diagnosticLevel}RecommendationCardHeader`} className="recommendation-card">
      <div>
        <div className="card-header">
          <h4 id={`${diagnosticLevel}RecommendationCardHeader`}>
            <b>{useELLDiagnostics && 'ELL '}{titleCase(diagnosticLevel)} Diagnostic</b>
            <span>Baseline Pre</span>
          </h4>
          {proficientDiagnosticRecord && <DemonstratedProficiencyFlag />}
          {studentIsRecommendedThisDiagnostic && <RecommendedFlag />}
        </div>
        <p className="description">{descriptionParagraphCopy()}</p>
      </div>
      <div>
        <p className="assignment">
          {
            previousAssignmentRecordToUse
              ? `You assigned this diagnostic to ${firstName} on ${formatDateToShortFormat(previousAssignmentRecordToUse.created_at)}`
              : `You haven’t assigned this diagnostic to ${firstName} yet.`
          }
        </p>
        <div className="button-wrapper">
          <a className="quill-button outlined gray small focus-on-light" href={previewLink(diagnosticLevel)} rel="noopener noreferrer" target="_blank">Preview</a>
          {assignOrViewResultsButton()}
        </div>
      </div>
    </section>
  )
};

export default IndividualStudentRecommendationCard;
